import { ReactElement, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Select } from '../../components/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../../components/Button';
import { EditIcon } from '../../components/icons/EditIcon';
import { Input } from '../../components/Input';
import { changesubjects } from '../../utils/ChangeArrays';
import axios from 'axios';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface FaleConoscoProps {
  perfilSelecionado: string;
}

const cpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;

function onlyDigits(value?: string) {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

function isValidCNPJ(cnpj: string) {
  if (!cnpj) return false;
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  const calc = (t: number) => {
    const n = cleaned.slice(0, t);
    let sum = 0;
    let pos = t - 7;
    for (let i = t; i >= 1; i--) {
      sum += Number(n[t - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const res = sum % 11;
    return res < 2 ? 0 : 11 - res;
  };

  const digit1 = calc(12);
  const digit2 = calc(13);
  return (
    digit1 === Number(cleaned.charAt(12)) &&
    digit2 === Number(cleaned.charAt(13))
  );
}

const faleConoscoFormSchema = z
  .object({
    isClient: z.boolean().optional(),
    name: z.string().min(1, { message: 'Campo obrigatório' }),
    cpf: z.string().regex(cpfRegex, { message: 'CPF inválido' }).optional(),
    cnpj: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val || val === '') return true;
          const digits = onlyDigits(val);
          return isValidCNPJ(digits);
        },
        { message: 'CNPJ inválido' },
      ),
    phone: z.string(),
    //.regex(/^\(\d{2}\) \d{5}-\d{4}$/, { message: 'Telefone inválido' }),
    subject: z.string(),
    email: z
      .string()
      .min(1, { message: 'Campo obrigatório' })
      .email({ message: 'Email inválido' }),
    message: z.string().min(1, { message: 'Campo obrigatório' }),
    subjectDetail: z.string(),
    solicitanteData: z.string(),
  })
  .superRefine((data, ctx) => {
    const cpfFilled = !!data.cpf && data.cpf.trim() !== '';
    const cnpjFilled = !!data.cnpj && data.cnpj.trim() !== '';
    if (cpfFilled && cnpjFilled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Preenchimento de CPF e CNPJ não permitido simultaneamente',
        path: ['cpf'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Preenchimento de CPF e CNPJ não permitido simultaneamente',
        path: ['cnpj'],
      });
    }
  });

type FaleConoscoFormSchema = z.infer<typeof faleConoscoFormSchema>;

const url = `${process.env.REACT_APP_OG_API_URL}/register/faleconosco`;

export default function FaleConoscoForm({
  perfilSelecionado,
}: FaleConoscoProps): ReactElement {
  const { register, handleSubmit, formState, setValue, watch } =
    useForm<FaleConoscoFormSchema>({
      defaultValues: { isClient: false },
      resolver: zodResolver(faleConoscoFormSchema),
    });

  const [selectedDoc, setSelectedDoc] = useState<'cpf' | 'cnpj'>('cpf');

  useEffect(() => {
    if (
      perfilSelecionado === 'clinica' ||
      perfilSelecionado === 'empresa' ||
      perfilSelecionado === 'corretora'
    ) {
      setSelectedDoc('cnpj');
      setValue('cnpj', '');
      setValue('cpf', '');
    } else {
      setSelectedDoc('cpf');
    }
  }, [perfilSelecionado, setValue]);

  const { errors } = formState;
  const [resp, setResp] = useState<{
    done: boolean;
    error: boolean;
    msg: string;
  }>({} as any);
  const [load, setLoad] = useState(false);
  const [subjectDetailOptions, setSubjectDetailOptions] = useState<string[]>(
    [],
  );
  const subject = watch('subject');
  const isClient = watch('isClient');
  const cpf = watch('cpf');
  const cnpj = watch('cnpj');
  const [disableToggle, setDisableToggle] = useState(false);
  const [disableSubjectAndDetail, setDisableSubjectAndDetail] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [detailedSubjects, setDetailedSubjects] = useState([]);
  const [file, setFile] = useState<File | null>(null); // Estado para o arquivo
  const [titular, setTitular] = useState(false);
  const [dependente, setDependente] = useState(false);
  const [success, setSuccess] = useState(false);
  const [protocolo, setProtocolo] = useState(0);

  const postRequest = async (url, data) => {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then(resp => resp.json());
    return resp.dados;
  };
  const getRequest = async url => {
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(r => r.json());

    // normalize payload: prefer resp.dados, then resp.data, then resp (array or object)
    const list =
      resp?.dados ?? resp?.data ?? (Array.isArray(resp) ? resp : null);

    if (Array.isArray(list) && list.length > 0) {
      const found =
        list.find(
          item =>
            Array.isArray(item.dependentes) &&
            item.dependentes.some(dependente => {
              // aceitar titular (codigoGrauParentesco === 1) mesmo que o codigoSituacao seja diferente
              // anteriormente aceitamos apenas 1 ou 16; alguns registros podem ter 2 (cancelado)
              return (
                dependente.codigoGrauParentesco === 1 &&
                // aceitar situacoes 1,16 ou 2; se quiser aceitar todas, remova a checagem
                (dependente.codigoSituacao === 1 ||
                  dependente.codigoSituacao === 16 ||
                  dependente.codigoSituacao === 2)
              );
            }),
        ) || list[0];
      return found;
    }

    // if resp is a single object representing the associado, return it
    if (resp && typeof resp === 'object') {
      return resp;
    }

    return null;
  };

  useEffect(() => {
    const url = `${process.env.REACT_APP_URL_BASE}/SYS/Services/Vendedor.aspx/Assuntos`;
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    const data = {
      token: token,
      tipoSolicitanteId: 2,
    };
    postRequest(url, data).then(resp => {
      changesubjects(resp);
      setSubjects(resp);
      setValue('solicitanteData', '7021');
    });
  }, []);

  useEffect(() => {
    if (subject) {
      const url = `${process.env.REACT_APP_URL_BASE}/SYS/Services/Vendedor.aspx/assuntosdetalhados`;
      const token = process.env.REACT_APP_TOKEN_S4E_V2;
      const data = {
        token: token,
        tipoSolicitanteId: 2,
        assuntoID: subject,
      };
      postRequest(url, data).then(resp => {
        changesubjects(resp);
        setDetailedSubjects(resp);
      });
    }
  }, [subject]);

  useEffect(() => {
    const cpfRegex = cpf?.replaceAll('.', '').replace('-', '');
    if (cpfRegex && cpfRegex.length === 11) {
      consultaCliente(cpfRegex);
    }
  }, [cpf]);

  useEffect(() => {
    const cleaned = cnpj?.replaceAll('.', '').replace('/', '').replace('-', '');
    if (cleaned && cleaned.length === 14) {
      consultaCnpj(cleaned);
    }
  }, [cnpj]);

  useEffect(() => {
    // when switching selected document, clear the other field and any populated client info
    if (selectedDoc === 'cpf') {
      setValue('cnpj', '');
    } else {
      setValue('cpf', '');
    }
    setValue('name', '');
    setValue('phone', '');
    setValue('email', '');
  }, [selectedDoc, setValue]);

  const consultaCliente = async (cpf: string) => {
    // always try cpfAssociado first
    let urlBase = `${process.env.REACT_APP_URL_BASE}/v2/api/Associados?token=${process.env.REACT_APP_TOKEN_S4E_V2}&cpfAssociado=${cpf}`;
    // if explicit dependent search desired, fallback
    if (!titular && dependente) {
      urlBase = `${process.env.REACT_APP_URL_BASE}/v2/api/Associados?token=${process.env.REACT_APP_TOKEN_S4E_V2}&cpfDependente=${cpf}`;
    }
    getRequest(urlBase).then(resp => {
      if (resp) {
        setValue('name', resp.nome);
        if (resp.contatos) {
          const telefone = resp.contatos.find(x => x.tipo === 'Celular');
          const email = resp.contatos.find(x => x.tipo === 'E-mail');
          setValue('phone', telefone.descricaoContato);
          setValue('email', email.descricaoContato);
        }
        const solicitanteId = resp.dependentes.find(
          x => x.codigoGrauParentesco === 1,
        );
        setValue('solicitanteData', String(solicitanteId.codigoDependente));
        const solicitante = watch('solicitanteData');
      } else {
        setValue('name', '');
        setValue('phone', '');
        setValue('email', '');
      }
    });
  };

  const consultaCnpj = async (cnpj: string) => {
    // tenta consultar por CNPJ (retorna estrutura similar à consulta por CPF)
    const urlBase = `${process.env.REACT_APP_URL_BASE}/v2/api/Associados?token=${process.env.REACT_APP_TOKEN_S4E_V2}&cnpjAssociado=${cnpj}`;
    getRequest(urlBase).then(resp => {
      if (resp) {
        setValue('name', resp.nome || '');
        if (resp.contatos) {
          const telefone = resp.contatos.find(x => x.tipo === 'Celular');
          const email = resp.contatos.find(x => x.tipo === 'E-mail');
          if (telefone) setValue('phone', telefone.descricaoContato);
          if (email) setValue('email', email.descricaoContato);
        }
        // se existir dependentes, tenta setar solicitanteData como no CPF
        if (resp.dependentes) {
          const solicitanteId = resp.dependentes.find(
            x => x.codigoGrauParentesco === 1,
          );
          if (solicitanteId)
            setValue('solicitanteData', String(solicitanteId.codigoDependente));
        }
      } else {
        setValue('name', '');
        setValue('phone', '');
        setValue('email', '');
      }
    });
  };

  const onSubmit = async (data: FaleConoscoFormSchema) => {
    setLoad(true);

    const formData = new FormData();
    formData.append('nome', data.name);
    formData.append('email', data.email);
    formData.append('telefone', data.phone);
    formData.append('descricaoOcorrencia', data.message);
    formData.append('motivoDetalhado', data.subjectDetail);
    formData.append('solicitante', JSON.stringify(data.solicitanteData));

    if (file) {
      formData.append('file', file);
    }

    try {
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.protocolo) {
        setResp({
          done: true,
          error: false,
          msg: response.data.message ?? 'Mensagem Enviada Com Sucesso!!!',
        });
        setProtocolo(response.data.protocolo);
        setSuccess(true);
      }
    } catch (error) {
      console.error(error);
      setResp({
        done: true,
        error: true,
        msg: 'Erro ao enviar mensagem. Verifique os campos e tente novamente.',
      });
    } finally {
      setLoad(false);
    }
  };

  const [expanded, setExpanded] = useState<string | false>(false);

  function handleChange(
    panel: string,
  ): (event: React.SyntheticEvent, isExpanded: boolean) => void {
    return (event, isExpanded) => {
      setExpanded(isExpanded ? panel : false);
    };
  }

  useEffect(() => {
    const cleaned = cnpj?.replaceAll('.', '').replace('/', '').replace('-', '');
    if (!cleaned || cleaned.length !== 14) return; // Não consulta se CNPJ vazio ou inválido
    consultaCnpj(cleaned);
  }, [cnpj]);

  return (
    <>
      {!success ? (
        <>
          <form id="ouvidoria-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6 mt-4">
              {!disableToggle && (
                <div className="flex items-center gap-6">
                  {/* <label className="font-medium">
                    Seja bem Vindo (a) ao nosso canal direto
                  </label> */}
                  <div className="flex items-center gap-2">
                    {/* <span className="font-semibold text-gray-600">
                      Documento selecionado:
                    </span> */}
                    <span
                      className={`px-3 py-0 rounded-full text-white font-bold ${
                        selectedDoc === 'cpf' ? 'bg-blue-600' : 'bg-blue-600'
                      }`}
                    >
                      {selectedDoc === 'cpf' ? 'CPF' : 'CNPJ'}
                    </span>
                  </div>

                  {/*<div className="flex items-center gap-4">
                     <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="docType"
                        checked={selectedDoc === 'cpf'}
                        onChange={() => setSelectedDoc('cpf')}
                      />
                      <span>CPF</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="docType"
                        checked={selectedDoc === 'cnpj'}
                        onChange={() => setSelectedDoc('cnpj')}
                      />
                      <span>CNPJ</span>
                    </label>
                  </div> */}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {selectedDoc === 'cpf' ? (
                  <div className="flex flex-col gap-4">
                    <Input
                      label="CPF"
                      placeholder="000.000.000-00"
                      activateMask
                      mask={'999.999.999-99'}
                      errorMessage={errors.cpf?.message as any}
                      {...register('cpf')}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <Input
                      label="CNPJ"
                      placeholder="00.000.000/0000-00"
                      activateMask
                      mask={'99.999.999/9999-99'}
                      errorMessage={errors.cnpj?.message as any}
                      {...register('cnpj')}
                    />
                  </div>
                )}
                <Input
                  label="Nome"
                  placeholder="Nome"
                  errorMessage={errors.name?.message as any}
                  {...register('name')}
                />
              </div>

              <div className="grid min-[748px]:grid-cols-2 grid-cols-1 min-[748px]:gap-8 gap-6">
                <Input
                  label="E-mail"
                  type="email"
                  placeholder="sample@email.com"
                  errorMessage={errors.email?.message as any}
                  {...register('email')}
                />
                <Input
                  label="Telefone"
                  placeholder="(00) 00000-0000"
                  activateMask
                  mask={'(99) 99999-9999'}
                  errorMessage={errors.phone?.message as any}
                  {...register('phone')}
                />
              </div>

              {!disableSubjectAndDetail && (
                <>
                  <div className="mb-4">
                    <label className="block font-semibold text-gray-700 mb-1">
                      Assunto
                    </label>
                    <span className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                      {perfilSelecionado === 'dentista' ||
                      perfilSelecionado === 'clinica'
                        ? 'FALE CONOSCO PRESTADOR – SITE'
                        : perfilSelecionado === 'empresa'
                        ? 'FALE CONOSCO EMPRESA – SITE'
                        : perfilSelecionado === 'corretora' ||
                          perfilSelecionado === 'vendedor'
                        ? 'FALE CONOSCO CORRETOR – SITE'
                        : 'Assunto não definido'}
                    </span>
                  </div>

                  <div className="mb-4">
                    <label className="block font-semibold text-gray-700 mb-1">
                      Assunto Detalhado
                    </label>
                    <span className="inline-block bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                      {perfilSelecionado === 'dentista' ||
                      perfilSelecionado === 'clinica'
                        ? 'FALE CONOSCO PRESTADOR – SITE'
                        : perfilSelecionado === 'empresa'
                        ? 'FALE CONOSCO EMPRESA – SITE'
                        : perfilSelecionado === 'corretora' ||
                          perfilSelecionado === 'vendedor'
                        ? 'FALE CONOSCO CORRETOR – SITE'
                        : 'Detalhe não definido'}
                    </span>
                  </div>

                  {/* <div className="grid grid-cols-1 gap-8">
                    <Select
                      label="Assunto"
                      placeholder="Selecione um assunto"
                      options={[]}
                      optionsFull={subjects}
                      errorMessage={errors.subject?.message}
                      {...register('subject')}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-8">
                    <Select
                      label="Assunto Detalhado"
                      placeholder="Assunto Detalhado"
                      options={[]}
                      optionsFull={detailedSubjects}
                      errorMessage={errors.subjectDetail?.message}
                      {...register('subjectDetail')}
                    />
                  </div> */}
                </>
              )}

              <div className="grid min-[748px]:grid-cols-1 grid-cols-1 gap-16 w-full">
                <Input
                  label="Mensagem"
                  textArea={true}
                  placeholder="Detalhar situação"
                  errorMessage={errors.message?.message as any}
                  {...register('message')}
                />
              </div>
            </div>
            {resp.done && (
              <p
                className={`mt-1 mb-2 text-base ${
                  !resp.error ? 'text-success-main' : 'text-destructive-main'
                }`}
              >
                {resp.msg}
              </p>
            )}
            <div className="flex items-center mt-4 flex flex-col sm:flex-row items-center gap-4 mt-4 w-full">
              <label className="cursor-pointer bg-primary-main text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors">
                Anexar Arquivo
                <input
                  type="file"
                  className="hidden"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                />
              </label>
              <Button
                disabled={load}
                className="ml-auto"
                text="Registrar Mensagem"
                intent="primary"
                Icon={EditIcon}
                type="submit"
              />
            </div>
          </form>
        </>
      ) : (
        <div
          className="flex items-center justify-center"
          style={{
            minHeight: '35vw',
            flexDirection: 'column',
            fontSize: 'x-large',
          }}
        >
          <div>
            <h1>Mensagem enviada com sucesso</h1>
          </div>
          <div>
            <p>
              Protocolo Gerado: <b>{protocolo}</b>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
